import { uniq } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { assertUnlessInProduction } from '@paragon/client-tools';
import { Point2D } from '@paragon/contract-library';
import { componentDesignsDeletedWithInsertions, projectSynchronized } from '@paragon/layout-models';
import { forceArray } from '@paragon/utilities';

import { pickSingleActiveBuildingGuid } from '../../library/hierarchy-filtering/active-building';
import { LayoutHierarchyFilteringState } from '../../library/hierarchy-filtering/filtering-state';
import { VisibilityConfig } from '../../library/hierarchy-filtering/visible-objects';
import {
  isConsideredDesignItem,
  isConsideredLayoutItem,
  SelectableItemType,
  SelectedItemIdentifier,
} from '../../library/selection/types';
import {
  getRelevantWindowToolModeFor,
  SelectionWindowMode,
  WindowToolTargetType,
} from '../../library/selection/window-tool/types';
import { sceneHoverCleared } from '../actions/sceneHoverActions';
import { setDimensionMode, startup } from './applicationSlice';
import { trussGroupsDeleted } from './trussGroupsSlice';

interface MutableSelections {
  // The component design that is visible in the main viewport.
  activeComponentDesignGuid: string | null;

  // While not selecting, hovering a component design or truss group from a list might mean we want to show information
  // in the scene, like highlighted truss envelopes associated with that design.
  hoveredComponentDesignGuidInList: string | null;
  hoveredTrussGroupGuidInList: string | null;

  // A jack component design whose connection marker is hovered in the scene, tracked for highlighting.
  hoveredJackComponentGuid: string | null;

  // The client-wide "selected item" system which supports multi-select.
  selectedItems: SelectedItemIdentifier[];

  // This property is relevant only to the check results panel, and has no relation to the "selected item" system.
  selectedCheckResultsLoadCaseGuid: string | null;

  // This property is relevant only for the dimensioning tool. Other usages of snaps, like point selection and various
  //   insertion modes, are handled elsewhere.
  selectedSnaps: SelectedDimensionSnaps;

  // 'ghost' indicates that objects not belonging to a level are visible. This is the expected mode when in a context
  //   without a layout hierarchy.
  layoutHierarchyFilter: LayoutHierarchyFilteringState | 'ghost';

  selectionWindowSettings: {
    [mode in SelectionWindowMode]: WindowToolTargetType;
  };

  // You are strongly discouraged from using this property for any other purpose than as a reference when switching
  //  between design and layout modes. `selectedItems` is the sole authority on what items are currently selected.
  mostRecentlySelectedLayoutModeItems: SelectedItemIdentifier[];

  // You are strongly discouraged from using this property for any other purpose than as a reference when switching
  //  between design and layout modes. `selectedItems` is the sole authority on what items are currently selected.
  mostRecentlySelectedDesignModeItems: SelectedItemIdentifier[];

  /**
   * Tracks whether the selection window tool is enabled (ie. the user is holding ctrl)
   */
  isSelectionWindowToolEnabled: boolean;

  /**
   * Tracks whether a selection window is actively being drawn in the scene. Distinct from
   *   `isSelectionWindowToolEnabled` so that we can lock down conflicting operations more aggressively.
   */
  isSelectionWindowToolActive: boolean;
}

export interface SelectedDimensionSnaps {
  snap1: Point2D | null;
  snap2: Point2D | null;
}

export type SelectionStore = Readonly<MutableSelections>;

const initialState: SelectionStore = {
  activeComponentDesignGuid: null,
  hoveredComponentDesignGuidInList: null,
  hoveredTrussGroupGuidInList: null,
  hoveredJackComponentGuid: null,
  selectedItems: [],
  selectedCheckResultsLoadCaseGuid: null,
  selectedSnaps: { snap1: null, snap2: null },

  layoutHierarchyFilter: 'ghost',

  selectionWindowSettings: {
    layout: 'trussEnvelope',
    design: 'member',
    webbing: 'webSegment',
  },

  mostRecentlySelectedLayoutModeItems: [],
  mostRecentlySelectedDesignModeItems: [],

  isSelectionWindowToolEnabled: false,
  isSelectionWindowToolActive: false,
};

const selectionsSlice = createSlice({
  name: 'selections',

  initialState,

  reducers: {
    // IMPORTANT: selection is complicated. Please don't dispatch this action directly. You should be using one of the
    //   thunk actions from `selectActions.ts` -- either `selectItem`, `selectItems`, `selectEntry`, or `selectEntries`.
    __UNSAFE_layoutItemsSelected: (
      state,
      action: PayloadAction<{
        itemGuids: string[];
        type: SelectableItemType;

        replacePreviousSelection: boolean;

        /**
         * Information required for updating the active layout hierarchy filters. Expected to be null unless in a layout
         *   with hierarchy.
         */
        hierarchyInfo:
          | {
              /**
               * The building that contains the objects being selected. Only a single building is currently supported.
               */
              containingBuildingGuid: string;

              /**
               * The level that contains the objects being selected. Must belong to the `containingBuildingGuid`. This will
               *   likely end up as the active level.
               */
              containingLevelGuid: string;

              /**
               * Other levels that are considered relevant to the selected object(s) and should be visible once this
               *   selection operation is complete. Must belong to the `containingBuildingGuid`.
               */
              relevantLevelGuids?: string[];

              /**
               * If true, an attempt will be made to respect the current hierarchy filter and exclusively show more levels
               *   as needed, even if the previous selection is being entirely replaced.
               *
               * This is not guaranteed to be possible, especially when the active building is being changed.
               *
               * Defaults to `false`.
               */
              attemptToPreservePreviousFilter?: boolean;
            }
          | null // A null `hierarchyInfo` indicates that there is no layout hierarchy in the active context.

          // A special case for when "ghost objects" that don't belong to any building are being selected in a hierarchy
          //   context.
          | 'ghost';
      }>,
    ) => {
      let { replacePreviousSelection } = action.payload;
      const { type, itemGuids, hierarchyInfo } = action.payload;

      const newlySelectedItems = itemGuids.map((guid) => ({ type, guid }));

      const activeBuildingIsChanging =
        hierarchyInfo &&
        ((hierarchyInfo === 'ghost') !== (state.layoutHierarchyFilter === 'ghost') ||
          (hierarchyInfo !== 'ghost' &&
            state.layoutHierarchyFilter !== 'ghost' &&
            hierarchyInfo.containingBuildingGuid !== state.layoutHierarchyFilter?.activeBuildingGuid));

      if (activeBuildingIsChanging) {
        replacePreviousSelection = true;
      }

      const selectedItems = replacePreviousSelection
        ? castDraft(newlySelectedItems)
        : state.selectedItems.concat(newlySelectedItems);

      state.selectedItems = selectedItems;

      const windowToolMode = getRelevantWindowToolModeFor(type);
      if (windowToolMode) {
        state.selectionWindowSettings[windowToolMode] = type as WindowToolTargetType;
      }

      if (isConsideredLayoutItem(type)) {
        state.mostRecentlySelectedLayoutModeItems = selectedItems;
      }

      if (hierarchyInfo == null) return;

      if (hierarchyInfo === 'ghost') {
        state.layoutHierarchyFilter = 'ghost';
        return;
      }

      const levelsThatMustBeVisible = hierarchyInfo.relevantLevelGuids
        ? uniq([hierarchyInfo.containingLevelGuid, ...hierarchyInfo.relevantLevelGuids])
        : [hierarchyInfo.containingLevelGuid];

      state.layoutHierarchyFilter = {
        visibleLevels: 'all',
        activeBuildingGuid: null,

        ...(typeof state.layoutHierarchyFilter === 'object' && state.layoutHierarchyFilter),
        activeLevelGuid: hierarchyInfo.containingLevelGuid,
      };

      if (hierarchyInfo.containingBuildingGuid) {
        state.layoutHierarchyFilter!.activeBuildingGuid = hierarchyInfo.containingBuildingGuid;
      }

      // We don't respect requests to preserve the active filter when:
      //   - The active building is changing -- clearly preserving old visible levels is impossible in this case.
      //   - More than one item is being selected -- during bulk selection we only want to show the active level.
      //
      // IMPORTANT: At times we have also ignored `attemptToPreservePreviousFilter` when a level is being selected, as
      //   our current interpretation is the user desires only the target level to be visible when they manually
      //   select it. This is no longer enforced here, because there are exceptions. Instead, the default value for
      //   `SelectionOptions.retainLayoutHierarchyFilters` is `false` during level selection and `true` otherwise,
      const filterPreservationImpossible = activeBuildingIsChanging || itemGuids.length > 1;

      if (filterPreservationImpossible || !hierarchyInfo.attemptToPreservePreviousFilter) {
        state.layoutHierarchyFilter.visibleLevels = VisibilityConfig.only(levelsThatMustBeVisible);
      } else {
        state.layoutHierarchyFilter.visibleLevels = VisibilityConfig.concat(
          state.layoutHierarchyFilter.visibleLevels,
          levelsThatMustBeVisible,
        );
      }
    },

    // IMPORTANT: selection is complicated. Please don't dispatch this action directly. You should be using one of the
    //   thunk actions from `selectActions.ts` -- either `selectItem`, `selectItems`, `selectEntry`, or `selectEntries`.
    __UNSAFE_designItemsSelected: (
      state,
      action: PayloadAction<{
        type: SelectableItemType;
        itemGuids: string[];

        replacePreviousSelection: boolean;

        activeComponentDesignGuid: string | null;
        activeAnalysisSetGuid: string | undefined;
        changingComponentDesign: boolean;
      }>,
    ) => {
      const { type } = action.payload;
      const newlySelectedItems = action.payload.itemGuids.map((guid) => ({ type, guid }));

      const selectedItems = action.payload.replacePreviousSelection
        ? castDraft(newlySelectedItems)
        : state.selectedItems.concat(newlySelectedItems);

      state.activeComponentDesignGuid = action.payload.activeComponentDesignGuid;

      state.selectedItems = selectedItems;
      state.mostRecentlySelectedDesignModeItems = selectedItems;

      const windowToolMode = getRelevantWindowToolModeFor(type);
      if (windowToolMode) {
        state.selectionWindowSettings[windowToolMode] = type as WindowToolTargetType;
      }
    },

    // IMPORTANT: selection is complicated. Please don't dispatch this action directly. Use ``resetSelectedItems` or one
    //   of the selection thunk actions mentioned above.
    __UNSAFE_itemsDeselected: (state, action: PayloadAction<{ type: SelectableItemType; itemGuids: string[] }>) => {
      const deselectionFilter = (item: SelectedItemIdentifier) =>
        action.payload.type === item.type && action.payload.itemGuids.every((guid) => guid !== item.guid);

      const proposedSelectedItems = state.selectedItems.filter(deselectionFilter);

      // Deselecting everything is not allowed. We assert here because this should have been prevented by the calling
      //   thunk action.
      //
      // If you REALLY REALLY need to accomplish this and don't have time to do it the right way, just dispatch
      //   `clearSelection` or use the `forceClearSelection` thunk action. Please don't do that though.
      if (proposedSelectedItems.length === 0) {
        assertUnlessInProduction(
          "Prevented an attempt to deselect every selected item. Someone either broke `selectEntries` or dispatched an __UNSAFE_ action when they shouldn't have.",
        );
        return;
      }

      state.selectedItems = state.selectedItems.filter(deselectionFilter);
      state.mostRecentlySelectedDesignModeItems = state.mostRecentlySelectedDesignModeItems.filter(deselectionFilter);
      state.mostRecentlySelectedLayoutModeItems = state.mostRecentlySelectedLayoutModeItems.filter(deselectionFilter);
    },

    clearSelection: (state) => {
      state.activeComponentDesignGuid = null;
      state.selectedItems = [];
      state.selectedCheckResultsLoadCaseGuid = null;
    },

    /** only suitable for use in layout mode; clearing the active design while visible would break everything */
    clearActiveComponentDesignGuid: (state) => {
      if (!state.selectedItems.some((item) => isConsideredDesignItem(item.type))) {
        state.activeComponentDesignGuid = null;
      }
    },

    // This action is not intended for direct usage. Use action creators from `levelVisibilityActions` or select a
    //   level / building / project instead.
    __UNSAFE_layoutHierarchyVisibilityAltered: (state, action: PayloadAction<LayoutHierarchyFilteringState>) => {
      state.layoutHierarchyFilter = action.payload;
    },

    ghostObjectVisibilityEnabled: (state) => {
      state.layoutHierarchyFilter = 'ghost';
    },

    selectCheckResultsLoadCase: (state, action: PayloadAction<string | null>) => {
      state.selectedCheckResultsLoadCaseGuid = action.payload;
    },

    selectSnaps: (state, action: PayloadAction<SelectedDimensionSnaps>) => {
      state.selectedSnaps = action.payload;
    },

    configureSelectionWindowTool: (
      state,
      action: PayloadAction<{ mode: SelectionWindowMode; selectedType: WindowToolTargetType }>,
    ) => {
      state.selectionWindowSettings[action.payload.mode] = action.payload.selectedType;
    },

    selectionWindowToolEnabled: (state) => {
      state.isSelectionWindowToolEnabled = true;
    },

    selectionWindowToolActivated: (state) => {
      state.isSelectionWindowToolEnabled = true;
      state.isSelectionWindowToolActive = true;
    },

    selectionWindowToolDeactivated: (state) => {
      state.isSelectionWindowToolEnabled = false;
      state.isSelectionWindowToolActive = false;
    },

    setHoveredComponentDesignGuidInList: (state, action: PayloadAction<string | null>) => {
      state.hoveredComponentDesignGuidInList = action.payload;
    },

    setHoveredTrussGroupGuidInList: (state, action: PayloadAction<string | null>) => {
      state.hoveredTrussGroupGuidInList = action.payload;
    },

    setHoveredJackComponentGuid: (state, action: PayloadAction<string | null>) => {
      state.hoveredJackComponentGuid = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(sceneHoverCleared, (state) => {
      state.hoveredJackComponentGuid = null;
    });

    builder.addCase(setDimensionMode, (state, action) => {
      if (!action.payload) {
        state.selectedSnaps = { snap1: null, snap2: null };
      }
    });

    builder.addCase(startup, (state, action) => {
      state.selectedItems =
        action.payload.activeComponentDesignGuid && action.payload.selectedItem ? [action.payload.selectedItem] : [];

      state.activeComponentDesignGuid = action.payload.activeComponentDesignGuid;
      state.selectedCheckResultsLoadCaseGuid = action.payload.selectedLoadCaseGuid;
    });

    // pick active building when models are loaded
    builder.addCase(projectSynchronized, (state, action) => {
      const projectGuids = Object.keys(action.payload);

      if (projectGuids.length === 0) return;
      if (projectGuids.length > 1)
        throw new Error(
          'Multiple projects synchronized into design client instance. This is unexpected and unsupported.',
        );

      const projectGuid = projectGuids[0];
      const syncPayload = action.payload[projectGuid];
      const incomingBuildingGuids = syncPayload.incomingModels
        .filter((model) => model.type === 'building')
        .map((building) => building.guid);

      if (
        state.layoutHierarchyFilter !== 'ghost' &&
        state.layoutHierarchyFilter.activeBuildingGuid === null &&
        incomingBuildingGuids.length > 0
      ) {
        state.layoutHierarchyFilter.activeBuildingGuid = pickSingleActiveBuildingGuid(incomingBuildingGuids);
      }
    });

    builder.addCase(trussGroupsDeleted, (state, action) => {
      if (state.hoveredTrussGroupGuidInList && forceArray(action.payload).includes(state.hoveredTrussGroupGuidInList)) {
        state.hoveredTrussGroupGuidInList = null;
      }
    });

    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      if (
        state.hoveredComponentDesignGuidInList &&
        action.payload.guids.includes(state.hoveredComponentDesignGuidInList)
      ) {
        state.hoveredComponentDesignGuidInList = null;
      }

      if (state.hoveredJackComponentGuid && action.payload.guids.includes(state.hoveredJackComponentGuid)) {
        state.hoveredJackComponentGuid = null;
      }

      if (state.activeComponentDesignGuid && action.payload.guids.includes(state.activeComponentDesignGuid)) {
        state.activeComponentDesignGuid = null;
      }
    });
  },
});

export const {
  __UNSAFE_designItemsSelected,
  __UNSAFE_itemsDeselected,
  __UNSAFE_layoutHierarchyVisibilityAltered,
  __UNSAFE_layoutItemsSelected,
  clearActiveComponentDesignGuid,
  clearSelection,
  configureSelectionWindowTool,
  ghostObjectVisibilityEnabled,
  selectCheckResultsLoadCase,
  selectionWindowToolActivated,
  selectionWindowToolDeactivated,
  selectionWindowToolEnabled,
  selectSnaps,
  setHoveredComponentDesignGuidInList,
  setHoveredJackComponentGuid,
  setHoveredTrussGroupGuidInList,
} = selectionsSlice.actions;
export const selections = selectionsSlice.reducer;
